import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Users, HardHat, Truck, AlertTriangle, Save, BadgeCheck, Settings2 } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { tradeLabel } from "@/lib/trades";

const TRADES = ["av_low_voltage", "electrical", "plumbing", "hvac", "general_contractor", "security_alarm", "fire_life_safety", "networking_it", "solar"];
const sel = "rounded-lg border border-slate-700 bg-[#0d1420] px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none";

export default function CrewAssignment({ jobId, onSaved }) {
  const [data, setData] = useState(null);
  const [primary, setPrimary] = useState("");
  const [helper, setHelper] = useState("");
  const [vehicle, setVehicle] = useState("");
  const [helperReq, setHelperReq] = useState(false);
  const [busy, setBusy] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);

  const load = async () => {
    const { data: d } = await api.get(`/jobs/${jobId}/assignment`);
    setData(d); setPrimary(d.primary_id || ""); setHelper(d.helper_id || "");
    setVehicle(d.vehicle_id || ""); setHelperReq(!!d.helper_required);
  };
  useEffect(() => { load().catch(() => {}); }, [jobId]);

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/jobs/${jobId}/assign`, { primary_id: primary || null, helper_id: helper || null, vehicle_id: vehicle || null, helper_required: helperReq });
      toast.success("Crew & vehicle assigned"); await load(); onSaved?.();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setBusy(false);
  };

  if (!data) return null;
  const techName = (id) => (data.techs.find((t) => t.id === id)?.name) || "";

  return (
    <div className="card-tac mt-6 p-5" data-testid="crew-assignment">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 font-mono-tac text-xs uppercase tracking-widest text-slate-500"><Users className="h-4 w-4 text-amber-400" /> Crew & vehicle assignment</div>
        <button data-testid="manage-quals-toggle" onClick={() => setManageOpen((o) => !o)} className="inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-white"><Settings2 className="h-3.5 w-3.5" /> Qualifications</button>
      </div>

      {(data.helper_required || data.auto_heavy) && (
        <div className="mb-4 flex items-start gap-2 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-200" data-testid="heavy-banner">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
          <div>Heavy equipment handling detected{data.heavy_reasons?.length ? ` (${data.heavy_reasons.join(", ")})` : ""}. A helper technician is recommended for safe multi-person lifting.</div>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-3">
        <div>
          <label className="text-xs text-slate-400">Primary technician</label>
          <select data-testid="assign-primary" value={primary} onChange={(e) => setPrimary(e.target.value)} className={`${sel} mt-1 w-full`}>
            <option value="">Unassigned</option>
            {data.techs.map((t) => <option key={t.id} value={t.id}>{t.name} {t.qualified ? "✓ qualified" : ""}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-slate-400">Helper technician {helperReq && <span className="text-amber-400">· required</span>}</label>
          <select data-testid="assign-helper" value={helper} onChange={(e) => setHelper(e.target.value)} className={`${sel} mt-1 w-full`}>
            <option value="">None</option>
            {data.techs.filter((t) => t.id !== primary).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-slate-400">Motor pool vehicle</label>
          <select data-testid="assign-vehicle" value={vehicle} onChange={(e) => setVehicle(e.target.value)} className={`${sel} mt-1 w-full`}>
            <option value="">No vehicle</option>
            {data.vehicles.map((v) => <option key={v.id} value={v.id} disabled={!v.available && v.id !== vehicle}>{v.name}{v.mpg ? ` · ${v.mpg} mpg` : ""}{v.status && v.status !== "available" ? ` (${v.status})` : ""}</option>)}
          </select>
        </div>
      </div>

      <label className="mt-3 flex items-center gap-2 text-xs text-slate-300">
        <input data-testid="helper-required-toggle" type="checkbox" checked={helperReq} onChange={(e) => setHelperReq(e.target.checked)} className="accent-amber-500" />
        Require a helper technician for this job (heavy / multi-person)
      </label>

      <div className="mt-4 flex items-center gap-3">
        <button data-testid="assign-save-btn" onClick={save} disabled={busy} className="btn-amber inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs disabled:opacity-50"><Save className="h-3.5 w-3.5" /> Save assignment</button>
        {primary && <span className="text-xs text-slate-400"><HardHat className="mr-1 inline h-3.5 w-3.5 text-cyan-400" />{techName(primary)}{helper ? ` + ${techName(helper)}` : ""}</span>}
        {vehicle && <span className="text-xs text-slate-400"><Truck className="mr-1 inline h-3.5 w-3.5 text-emerald-400" />{data.vehicles.find((v) => v.id === vehicle)?.name}</span>}
      </div>

      {manageOpen && <QualificationsEditor techs={data.techs} onSaved={load} />}
    </div>
  );
}

function QualificationsEditor({ techs, onSaved }) {
  const [saving, setSaving] = useState(null);
  const [comm, setComm] = useState({});
  const toggle = async (t, trade) => {
    const has = (t.certifications || []).includes(trade);
    const next = has ? t.certifications.filter((c) => c !== trade) : [...(t.certifications || []), trade];
    setSaving(t.id);
    try { await api.patch(`/members/${t.id}/certifications`, { certifications: next }); toast.success(`Updated ${t.name}'s quals`); onSaved?.(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setSaving(null);
  };
  const saveComm = async (t) => {
    const val = comm[t.id] ?? t.commission_pct ?? 0;
    setSaving(t.id);
    try { await api.patch(`/members/${t.id}/commission`, { commission_pct: Number(val) }); toast.success(`${t.name} commission ${val}%`); onSaved?.(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setSaving(null);
  };
  return (
    <div className="mt-4 rounded-xl border border-slate-800 bg-[#0d1420] p-4" data-testid="quals-editor">
      <div className="mb-2 flex items-center gap-2 font-mono-tac text-[10px] uppercase tracking-widest text-slate-500"><BadgeCheck className="h-3.5 w-3.5 text-emerald-400" /> Technician qualifications & commission</div>
      <div className="space-y-3">
        {techs.map((t) => (
          <div key={t.id} data-testid={`quals-member-${t.id}`}>
            <div className="mb-1 flex flex-wrap items-center gap-2 text-xs font-semibold text-white">
              {t.name} <span className="font-mono-tac text-[10px] uppercase text-slate-500">{t.role}</span>{saving === t.id && <span className="text-[10px] text-amber-400">saving…</span>}
              {t.role !== "subcontractor" && (
                <span className="ml-auto inline-flex items-center gap-1 text-[10px] text-slate-400">commission
                  <input data-testid={`commission-${t.id}`} type="number" defaultValue={t.commission_pct || 0} onChange={(e) => setComm({ ...comm, [t.id]: e.target.value })}
                    className="w-14 rounded border border-slate-700 bg-[#0b0f17] px-1.5 py-0.5 text-white" />%
                  <button data-testid={`commission-save-${t.id}`} onClick={() => saveComm(t)} className="rounded-full border border-slate-700 px-2 py-0.5 text-[10px] text-slate-300 hover:border-amber-500">set</button>
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {TRADES.map((tr) => {
                const on = (t.certifications || []).includes(tr);
                return <button key={tr} data-testid={`qual-${t.id}-${tr}`} onClick={() => toggle(t, tr)}
                  className={`rounded-full px-2 py-0.5 font-mono-tac text-[10px] transition-colors ${on ? "bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-600/40" : "bg-slate-800/70 text-slate-400 hover:text-white"}`}>{tradeLabel(tr)}</button>;
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
